import { Button } from "@chakra-ui/react";
import { EqualizationPreviewTable } from "features/closings/tabs/ClosingTabEqualizations";
import { CommitmentCancelCorrectPageProps } from "features/commitments/cancel-correct/CommitmentCancelCorrectPageProps";
import {
  useEqualizationCreate,
  useIsCancelCorrectEqualizationAvailableRetrieveSuspense,
} from "gen/endpoints/equalization/equalization";
import { useFundFamilySubEventList } from "gen/endpoints/fund-family-event/fund-family-event";
import { useSecurityOwnershipCancelCorrectFinalize } from "gen/endpoints/security-ownership/security-ownership";
import { FundFamilySubEventTypeEnum } from "gen/model/fundFamilySubEventTypeEnum";
import { find } from "lodash-es";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router-dom";
import EmptyState from "shared/components/empty/EmptyState";
import Form from "shared/components/form/Form";
import { PageWizardFormFooter } from "shared/components/layout/PageWizard";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { useToast } from "shared/components/toast/useToast";
import { rootRoute } from "shared/navigation/Routes";

export const CommitmentCancelCorrectEqualizationPage: React.FC = () => {
  const { showToast, showErrorToast } = useToast();
  const { t } = useTranslation(["common", "closing"]);
  const { fundFamilyId, fundFamilyEventId, commitmentId } =
    useOutletContext<CommitmentCancelCorrectPageProps>();
  const navigate = useNavigate();

  const { mutateAsync: finalizeCancelCorrect } =
    useSecurityOwnershipCancelCorrectFinalize();

  const { data: isCancelCorrectEqualizationAvailable } =
    useIsCancelCorrectEqualizationAvailableRetrieveSuspense(
      fundFamilyId,
      fundFamilyEventId,
    );

  const isEqualizationAvailable =
    isCancelCorrectEqualizationAvailable.is_cancel_correct_equalization_available;

  const {
    mutateAsync: createEqualization,
    isPending: isCreateEqualizationLoading,
  } = useEqualizationCreate({
    mutation: {
      onMutate: () =>
        showToast({
          message: t("closing:equalizationPreviewCreate"),
          variant: "info",
        }),
      onError: showErrorToast,
    },
  });

  const { data: subEvents, refetch: refetchSubEvents } =
    useFundFamilySubEventList(fundFamilyId, fundFamilyEventId);

  const equalizationSubEvent = find(
    subEvents,
    (subEvent) => subEvent.type === FundFamilySubEventTypeEnum.equalization,
  );

  const refreshEqualization = async () => {
    await createEqualization({
      fundFamilyId,
      fundFamilyEventId,
      data: {},
    });
    await refetchSubEvents();
  };

  const runEqualizationButton = (
    <Button
      colorScheme="primary"
      size="xs"
      onClick={refreshEqualization}
      isLoading={isCreateEqualizationLoading}
    >
      {isEqualizationAvailable
        ? t("equalization:runEqualization.button")
        : t("equalization:recalculateWaterfall.button")}
    </Button>
  );

  const form = useForm();
  const onSubmit = async () => {
    await finalizeCancelCorrect({
      fundFamilyId,
      securityOwnershipId: commitmentId,
      params: {
        fund_family_event_id: fundFamilyEventId,
      },
    });
  };

  const tableWhenEqualizationIsAvailable = equalizationSubEvent ? (
    <EqualizationPreviewTable
      fundFamilyId={fundFamilyId}
      fundFamilyEventId={fundFamilyEventId}
      equalizationSubEvent={equalizationSubEvent}
      isCreateEqualizationLoading={isCreateEqualizationLoading}
    />
  ) : (
    <EmptyState title={t("closing:equalizationPreviewEmptyTitle")}>
      {runEqualizationButton}
    </EmptyState>
  );

  const tableWhenEqualizationIsNotAvailable = (
    <EmptyState
      title={t("equalization:recalculateWaterfall.title")}
      description={t("equalization:recalculateWaterfall.description")}
    >
      {runEqualizationButton}
    </EmptyState>
  );

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      onSuccess={() =>
        navigate(
          rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .commitments({})
            .commitment({ commitmentId }).$,
        )
      }
    >
      <TableBox>
        {isEqualizationAvailable && (
          <TableBoxHeader>
            <TableBoxTitle>{t("common:equalization")}</TableBoxTitle>
          </TableBoxHeader>
        )}
        <TableBoxBody>
          {isEqualizationAvailable
            ? tableWhenEqualizationIsAvailable
            : tableWhenEqualizationIsNotAvailable}
        </TableBoxBody>
      </TableBox>
      <PageWizardFormFooter />
    </Form>
  );
};
